import React, { useEffect, useState } from 'react';
import { ChevronDown, GraduationCap } from 'lucide-react';
import api from '../api';
import { toast } from 'sonner';

/**
 * VerticalSelector — renders the student's currently-active vertical as a
 * pill trigger. Clicking opens a dropdown of every vertical the student is
 * enrolled in. Selecting a new vertical persists to the server and calls
 * onChange so the parent dashboard can refetch scoped data.
 *
 * For students with only 1 vertical, shows a static badge (no dropdown).
 */
const VerticalSelector = ({ onChange }) => {
  const [loading, setLoading] = useState(true);
  const [verticals, setVerticals] = useState([]);
  const [active, setActive] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/student/verticals');
        if (!mounted) return;
        setVerticals(res.data.verticals || []);
        const initial = res.data.active_vertical || '';
        setActive(initial);
        if (onChange && initial) onChange(initial);
      } catch {
        // student without verticals seeded — silent
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = async (vertical) => {
    if (vertical === active) { setOpen(false); return; }
    try {
      await api.put('/student/active-vertical', { vertical });
      setActive(vertical);
      setOpen(false);
      toast.success(`Switched to ${vertical}`);
      if (onChange) onChange(vertical);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to switch vertical');
    }
  };

  if (loading || verticals.length === 0) return null;

  // Single-vertical students → static badge, no interaction
  if (verticals.length === 1) {
    return (
      <div className="inline-flex items-center gap-2 mt-2 px-3 py-1 rounded-full bg-slate-100 border border-slate-200" data-testid="vertical-badge-single">
        <GraduationCap className="w-3.5 h-3.5 text-slate-600" strokeWidth={2.5} />
        <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">{active || verticals[0].vertical}</span>
      </div>
    );
  }

  const activeMeta = verticals.find(v => v.vertical === active) || verticals[0];

  return (
    <div className="relative inline-block mt-2" data-testid="vertical-selector">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border-2 border-slate-900 text-slate-900 font-bold text-xs uppercase tracking-wider shadow-[2px_2px_0px_rgba(15,23,42,0.9)] hover:shadow-[3px_3px_0px_rgba(15,23,42,0.9)] hover:-translate-y-0.5 transition-all"
        data-testid="vertical-selector-trigger"
      >
        <GraduationCap className="w-3.5 h-3.5" strokeWidth={2.5} />
        <span>{active}</span>
        <span className="text-[10px] font-semibold text-slate-500 normal-case tracking-normal">
          · {activeMeta?.teacher_name}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={2.5} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-2 left-0 min-w-[280px] bg-white border-2 border-slate-900 rounded-xl shadow-[4px_4px_0px_rgba(15,23,42,0.9)] py-1 overflow-hidden" data-testid="vertical-selector-menu">
            {verticals.map(v => {
              const isActive = v.vertical === active;
              return (
                <button
                  key={v.vertical}
                  onClick={() => handleSelect(v.vertical)}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between gap-3 ${isActive ? 'bg-slate-900 text-white' : 'hover:bg-slate-100 text-slate-900'}`}
                  data-testid={`vertical-option-${v.vertical}`}
                >
                  <div className="flex flex-col">
                    <span className="font-bold">{v.vertical}</span>
                    <span className={`text-[11px] ${isActive ? 'text-slate-300' : 'text-slate-500'}`}>
                      {v.teacher_name} · {v.classes_consumed}/{v.classes_purchased} classes
                    </span>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${v.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                    {v.status}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default VerticalSelector;
